import { Banknote } from 'lucide-react';
import EmptyState from '../../shared/EmptyState';
import DataTable from '../../shared/DataTable';
import TableSpinner from '../../shared/TableSpinner';

const formatDate = (val) => {
  if (!val) return '--';
  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? val : d.toLocaleDateString();
  } catch {
    return val;
  }
};

const formatCurrency = (value, currency) => {
  if (value == null || value === '') return '--';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return currency
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: currency?.code || currency?.symbol || currency }).format(num)
    : num.toLocaleString();
};

const getFundCommitmentLabel = (fc) => {
  if (!fc) return '--';
  const amt = fc.amount != null ? fc.amount : '';
  const proj = fc.project?.name || fc.project?.projectCode || '';
  const contract = fc.contract?.contractNo || fc.contract?.contractName || '';
  return [amt, proj, contract].filter(Boolean).join(' - ') || fc._id;
};

const FundDisbursementIcon = () => <Banknote className="h-16 w-16 text-slate-400" strokeWidth={1.5} />;

const FundDisbursementsTable = ({ data, isLoading, isFetching, isError, error, onEdit, onDelete, isBusy = false, deletingId }) => {
  if (isLoading) return <TableSpinner message="Loading fund disbursements…" />;
  if (isError)
    return (
      <EmptyState
        isError
        title="Error loading fund disbursements"
        description={error?.response?.data?.message || error?.message || 'Unable to load fund disbursements.'}
        icon={<FundDisbursementIcon />}
      />
    );
  if (!data?.length)
    return (
      <EmptyState
        title="No fund disbursements found"
        description="Adjust filters or create one to see it listed here."
        icon={<FundDisbursementIcon />}
      />
    );

  const columns = [
    { key: 'amount', header: 'Amount', render: (r) => <span className="font-semibold text-slate-700">{formatCurrency(r.amount, r.currency)}</span> },
    { key: 'currency', header: 'Currency', render: (r) => <span className="text-slate-600">{r.currency?.code || r.currency?.name || '--'}</span> },
    { key: 'date', header: 'Date', render: (r) => <span className="text-slate-600">{formatDate(r.date)}</span> },
    { key: 'fundCommitment', header: 'Fund Commitment', render: (r) => <span className="text-slate-600">{getFundCommitmentLabel(r.fundCommitment)}</span> },
    { key: 'paymentProof', header: 'Payment Proof', render: (r) => <span className="text-slate-600 max-w-[200px] truncate block" title={r.paymentProof}>{r.paymentProof || '--'}</span> },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (item) => (
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => onEdit?.(item)} className="inline-flex items-center gap-1 rounded-lg border border-cream/60 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-60" disabled={isBusy}>Edit</button>
          <button type="button" onClick={() => onDelete?.(item)} className="inline-flex items-center gap-1 rounded-lg border border-red-100 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60" disabled={isBusy}>{isBusy && deletingId === item._id ? 'Removing…' : 'Delete'}</button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      emptyTitle="No fund disbursements found"
      emptyMessage="Adjust filters or create one to see it listed here."
      emptyIcon={<FundDisbursementIcon />}
    />
  );
};

export default FundDisbursementsTable;
